import { useState } from "react";
import { toast } from "sonner";
import { Banknote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { ResourceTable } from "@/components/data/ResourceTable";
import { RefSelect } from "@/components/data/FieldInput";
import { PageHeader } from "@/components/data/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Situation, Rapprochement } from "@/components/finance/Situation";
import { api, errMsg, fmtDate, fmtMoney, today } from "@/lib/api";

const COMPTE = { name: "compte_id", type: "ref", ref: "comptes" };

function ReglementDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ montant: "", compte_id: null, date: today() });
  const submit = async () => {
    try {
      await api.post(`/dettes/${row.id}/reglement`, f);
      toast.success("Règlement enregistré");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reglement-dialog">
        <DialogHeader>
          <DialogTitle>Règlement {row?.numero}</DialogTitle>
          <DialogDescription>Reste à régler : {fmtMoney(row?.reste)}. {row?.type === "creance" ? "Encaissement" : "Décaissement"} sur le compte choisi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Montant</Label><Input type="number" className="font-mono" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} data-testid="reglement-montant-input" /></div>
          <div><Label>Compte</Label><div className="mt-1"><RefSelect field={COMPTE} value={f.compte_id} onChange={(v) => setF({ ...f, compte_id: v })} testId="reglement-compte-select" /></div></div>
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.montant || !f.compte_id} data-testid="reglement-submit-button">Enregistrer le règlement</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Reglements({ row }) {
  return (
    <div className="mt-8">
      <h4 className="mb-3 text-xs font-bold uppercase tracking-wider">Règlements</h4>
      <div className="border rounded-md divide-y text-xs">
        {(row.reglements || []).map((r) => (
          <div key={r.id} className="p-2.5 flex justify-between"><span>{fmtDate(r.date)} · {r.user_name}</span><span className="font-mono font-semibold">{fmtMoney(r.montant)}</span></div>
        ))}
        {!row.reglements?.length && <div className="p-3 text-muted-foreground">Aucun règlement</div>}
      </div>
    </div>
  );
}

export default function DettesPage() {
  const { can } = useAuth();
  const [reg, setReg] = useState(null);
  const [reload, setReload] = useState(0);
  const extraActions = (row) => [
    { key: "reglement", label: "Enregistrer un règlement", icon: Banknote,
      show: can("dettes", "valider") && row.statut === "valide" && row.reste > 0, onClick: setReg },
  ];
  return (
    <div>
      <PageHeader title="Dettes & créances" subtitle="Dettes envers superviseurs et revendeurs, créances sur superviseurs, dette partenaire, et rapprochement des montants." />
      <Tabs defaultValue="situation">
        <TabsList className="mb-4 flex-wrap h-auto"><TabsTrigger value="situation" data-testid="tab-situation">Situation</TabsTrigger><TabsTrigger value="liste" data-testid="tab-dettes-liste">Pièces dettes / créances</TabsTrigger><TabsTrigger value="rapprochement" data-testid="tab-rapprochement">Rapprochement</TabsTrigger></TabsList>
        <TabsContent value="situation"><Situation /></TabsContent>
        <TabsContent value="liste"><ResourceTable res="dettes" extraActions={extraActions} reloadKey={reload} detailExtra={(row) => <Reglements row={row} />} /></TabsContent>
        <TabsContent value="rapprochement"><Rapprochement /></TabsContent>
      </Tabs>
      {reg && <ReglementDialog row={reg} onClose={() => setReg(null)} onDone={() => setReload((n) => n + 1)} />}
    </div>
  );
}
